import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import type {
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserSession,
  ICognitoStorage,
} from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import 'crypto-js/lib-typedarrays';
import { assert } from 'ts-essentials';

import { COOKIE_NAME_KEY_AUTH } from '@src/constants/cookie-names';
import {
  AWS_REGION,
  COGNITO_APP_CLIENT_ID,
  COGNITO_DOMAIN,
  COGNITO_IDENTITY_POOL_ID,
  COGNITO_USER_POOL_ID,
} from '@src/features/auth/constants';
import { getLoginUrl } from '@src/features/auth/utils/auth';
import { MinimizedCookieStorage, getCookieSettings } from '@src/features/auth/utils/cookies';
import { removeProfileCache } from '@src/features/profile/lib/profile-cache';
import { deleteCookie } from '@src/utils/cookies';

import { AuthException } from './exceptions';
import {
  AuthConfig,
  AuthEventMetaData,
  AuthSignUpAttributes,
  IAuthService,
  LogoutOptions,
  SocialProvider,
} from './i-auth-service';

type CognitoAuthError = {
  message: string;
  code:
    | 'NotAuthorizedException'
    | 'CodeMismatchException'
    | 'UserNotFoundException'
    | 'ExpiredCodeException'
    | 'UsernameExistsException'
    | 'UserNotConfirmedException'
    | 'LimitExceededException'
    | 'InvalidParameterException';
};

const errorMap: Record<CognitoAuthError['code'], AuthException['code']> = {
  CodeMismatchException: 'InvalidCode',
  ExpiredCodeException: 'InvalidCode',
  NotAuthorizedException: 'InvalidCredentials',
  UserNotConfirmedException: 'NotConfirmed',
  UsernameExistsException: 'EmailAlreadyExists',
  UserNotFoundException: 'InvalidCredentials',
  LimitExceededException: 'LimitExceeded',
  InvalidParameterException: 'NotConfirmed',
};

const cognitoProvider: Record<SocialProvider, CognitoHostedUIIdentityProvider> = {
  google: CognitoHostedUIIdentityProvider.Google,
  facebook: CognitoHostedUIIdentityProvider.Facebook,
  apple: CognitoHostedUIIdentityProvider.Apple,
};

export class CognitoService implements IAuthService {
  private logoutUrl = '';

  private storage?: ICognitoStorage;

  constructor(private amplifyAuth: typeof Auth) {}

  async signUp(email: string, password: string, metaData: AuthEventMetaData, { fullName }: AuthSignUpAttributes) {
    await this.handleAuthError(async () => {
      const { user } = await this.amplifyAuth.signUp({
        username: email,
        password,
        attributes: {
          name: fullName,
        },
        clientMetadata: metaData,
      });

      /* Due to this [Amplify issue](https://github.com/aws-amplify/amplify-js/issues/6320) automatic login
       * after confirmation is not possible. As a workaround the password is temporarily stored in localstorage.
       */
      localStorage.setItem('password', password);

      return user;
    });
  }

  async login(email: string, password: string) {
    return this.handleAuthError(async () => {
      await this.amplifyAuth.signOut();

      const signInResult = await this.amplifyAuth.signIn({
        username: email,
        password,
      });

      setTimeout(() => {
        deleteCookie(COOKIE_NAME_KEY_AUTH);
      }, 2000);

      return signInResult;
    });
  }

  async logout({ isCrossDomain, continueUrl }: LogoutOptions) {
    const user = await this.getAuthUser();

    if (continueUrl) {
      const loginUrl = getLoginUrl();
      const logoutUrl = new URL(continueUrl);

      this.configure({ loginUrl, logoutUrl: logoutUrl.origin });
    }

    // After logging out, to avoid still having the user cookies after cognito redirect
    // due racing conditions, we need to wait a bit before redirecting
    setTimeout(() => {
      if (continueUrl) {
        window.location.href = continueUrl ?? new URL('/auth', window.origin).href;
      } else {
        window.location.reload();
      }
    }, 800);

    if (user?.externalProvider && !isCrossDomain) {
      window.location.href = this.oAuthLogoutUrl;
    }

    await this.amplifyAuth.signOut();
    removeProfileCache();
  }

  async loginWithSocialProvider(provider: SocialProvider) {
    await this.amplifyAuth.federatedSignIn({
      provider: cognitoProvider[provider],
    });

    setTimeout(() => {
      deleteCookie(COOKIE_NAME_KEY_AUTH);
    }, 2000);
  }

  async forgotPassword(email: string, metaData: AuthEventMetaData) {
    await this.handleAuthError(async () => this.amplifyAuth.forgotPassword(email, metaData));
  }

  async forgotPasswordSubmit(email: string, password: string, verificationCode: string) {
    await this.handleAuthError(async () => this.amplifyAuth.forgotPasswordSubmit(email, verificationCode, password));
  }

  async changePassword(oldPassword: string, newPassword: string) {
    await this.handleAuthError(async () => {
      const user: CognitoUser | null = await this.amplifyAuth.currentAuthenticatedUser();

      await this.amplifyAuth.changePassword(user, oldPassword, newPassword);
    });
  }

  async getAuthUser() {
    try {
      const user: CognitoUser = await this.amplifyAuth.currentAuthenticatedUser();

      const attributes = await new Promise<CognitoUserAttribute[]>((resolve, reject) => {
        user?.getUserAttributes((error, data) => {
          if (data) {
            resolve(data as CognitoUserAttribute[]);
            return;
          }
          reject(error);
        });
      });

      return {
        email: attributes.find(({ Name }) => Name === 'email')?.Value,
        externalProvider: this.storage?.getItem('amplify-signin-with-hostedUI') === 'true',
      };
    } catch (e) {
      return null;
    }
  }

  async getAuthToken() {
    try {
      const cognitoUser: CognitoUser = await this.amplifyAuth.currentAuthenticatedUser();

      return cognitoUser.getSignInUserSession()?.getIdToken().getJwtToken() ?? null;
    } catch (e) {
      return null;
    }
  }

  async refreshAuthToken() {
    const [cognitoUser, currentSession] = await Promise.all<[CognitoUser, CognitoUserSession]>([
      await this.amplifyAuth.currentAuthenticatedUser(),
      await this.amplifyAuth.currentSession(),
    ]);
    const refreshToken = currentSession.getRefreshToken();

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    cognitoUser.refreshSession(refreshToken, () => {});
  }

  async resendEmailChangeVerification(metaData: AuthEventMetaData): Promise<void> {
    await this.handleAuthError(async () => {
      const user: CognitoUser = await this.amplifyAuth.currentUserPoolUser();
      await this.amplifyAuth.verifyUserAttribute(user, 'email', metaData);
    });
  }

  async confirmUserEmail(code: string) {
    await this.handleAuthError(async () => this.amplifyAuth.verifyCurrentUserAttributeSubmit('email', code));
  }

  async confirmSignUp(email: string, verificationCode: string) {
    await this.handleAuthError(async () => this.amplifyAuth.confirmSignUp(email, verificationCode));
  }

  async loginAfterSignUp(email: string) {
    const password = localStorage.getItem('password');
    assert(password);

    await this.handleAuthError(async () => {
      await this.amplifyAuth.signOut();
      await this.amplifyAuth.signIn(email, password);
      localStorage.removeItem('password');
    });
  }

  hasSignedUpOnSameDevice() {
    const password = localStorage.getItem('password');
    return !!password;
  }

  async resendVerification(email: string, metaData: AuthEventMetaData): Promise<void> {
    await this.amplifyAuth.resendSignUp(email, metaData);
  }

  async updateUserEmail(newEmail: string, metaData: AuthEventMetaData): Promise<void> {
    const isEmailAlreadyExisting = await this.doesEmailExist(newEmail);
    if (isEmailAlreadyExisting) {
      throw new AuthException('EmailAlreadyExists', 'email already exists');
    }

    const cognitoUser: CognitoUser | null = await this.amplifyAuth.currentAuthenticatedUser();
    await this.amplifyAuth.updateUserAttributes(
      cognitoUser,
      {
        email: newEmail,
      },
      metaData,
    );
  }

  async doesEmailExist(email: string): Promise<boolean> {
    try {
      // Attempt un-successful signup confirmation. it will raise a UserNotFoundException if the user does not exist.
      // Requires "PreventUserExistenceErrors" to be set in the app client's settings
      await this.amplifyAuth.confirmSignUp(email, '000000', {
        forceAliasCreation: false,
      });
      return false;
    } catch (e) {
      return (e as CognitoAuthError).code !== 'UserNotFoundException';
    }
  }

  configure({ loginUrl, logoutUrl }: AuthConfig) {
    const oauth = {
      domain: COGNITO_DOMAIN,
      redirectSignIn: loginUrl,
      redirectSignOut: logoutUrl,
      responseType: 'code',
      scope: ['email', 'openid', 'profile', 'phone', 'aws.cognito.signin.user.admin'],
      urlOpener: (url: string) => this.urlOpener(url),
    };

    const storage = new MinimizedCookieStorage(getCookieSettings(loginUrl));

    this.amplifyAuth.configure({
      aws_project_region: AWS_REGION,
      aws_cognito_region: AWS_REGION,
      aws_cognito_identity_pool_id: COGNITO_IDENTITY_POOL_ID,
      aws_user_pools_id: COGNITO_USER_POOL_ID,
      aws_user_pools_web_client_id: COGNITO_APP_CLIENT_ID,
      oauth,
      storage,
    });
    this.logoutUrl = logoutUrl;
    this.storage = storage;
  }

  get oAuthLogoutUrl() {
    const oAuthLogoutUrl = new URL(`https://${COGNITO_DOMAIN}/logout`);
    oAuthLogoutUrl.searchParams.set('client_id', COGNITO_APP_CLIENT_ID);
    oAuthLogoutUrl.searchParams.set('logout_uri', this.logoutUrl);

    return oAuthLogoutUrl.href;
  }

  private async urlOpener(url: string) {
    // Handle oAuth logout redirect manually
    if (url === this.oAuthLogoutUrl) {
      return Promise.resolve();
    }

    return Promise.resolve(window.open(url, '_self'));
  }

  private async handleAuthError<T>(callback: () => Promise<T>) {
    try {
      return await callback();
    } catch (e) {
      assert(((err): err is CognitoAuthError => true)(e));

      throw new AuthException(errorMap[e.code] ?? errorMap.NotAuthorizedException, e.message);
    }
  }
}
